import { useRef, useState, useEffect, useCallback, useContext } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import VideoPlayer from '@/components/VideoPlayer'
import ListHover from '@/components/ListHover'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const HomePage = ({
  stub,
}) => {
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const { getImageUrl, setPageAnimationReady } = useContext(GeneralContext)

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, strings } = useSelector((state) => ({
    current: state.page.current,
    strings: state.language.string,
    page: state.page.pages[stub] || {},
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.hph_img, 'full', stub, 'pages')
    preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Cover Home
  ------------------------------*/
  const renderCoverHome = () => {
    return (page.hph_title && page.hph_img) && (
      <Hero
        aboveTheFold={true}
        img={getImageUrl(page.hph_img, 'full', stub, 'pages')}
        label={page.hph_label}
        link={page.hph_link}
        text={page.hph_text}
        title={page.hph_title}
        fontSize="sm"
        home
      />
    )
  }

  /*------------------------------
  Render Video Section
  ------------------------------*/
  const renderVideo = () => {
    return page.video_url && (
      <div className={classes.containerVideo}>
        <VideoPlayer
          video={page.video_url}
          controllers
          poster={getImageUrl(page.video_poster_img, 'large', stub, 'pages')}
        />
      </div>
    )
  }

  /*------------------------------
  Render Focus
  ------------------------------*/
  const renderFocus = () => {
    return (
      <Hero
        background="dark"
        borderTitle
        fontSize="lg"
        img={getImageUrl(page.focus_img, 'large', stub, 'pages')}
        label={page.focus_label}
        link={page.focus_link}
        pretitle={page.focus_pretitle}
        text={page.focus_text}
        title={page.focus_title}
        imgSize="crop"
        render
        home
      />
    )
  }

  /*------------------------------
  Render Home Hover List
  ------------------------------*/
  const renderHomeHoverList = () => {
    return (
      <div
        className={classNames({
          [classes.wrapper]: true,
          [classes.hoverList]: true,
        })}
      >
        <div className={classes.pretitle}>{strings.products_label}</div>
        <ListHover />
      </div>
    )
  }

  /*------------------------------
  Render Focus Project
  ------------------------------*/
  const renderFocusProject = () => {
    return page.focusproject_title && (
      <Hero
        borderTitle
        fontSize="sm"
        img={getImageUrl(page.focusproject_img, 'large', stub, 'pages')}
        link={page.focusproject_link}
        pretitle={page.focusproject_pretitle}
        text={cutExcerpt(page.focusproject_text)}
        title={page.focusproject_title.replace(/\n/g, '<br />')}
      />
    )
  }

  /*------------------------------
  Render Focus Art
  ------------------------------*/
  const renderFocusArt = () => {
    return page.focusart_title && (
      <Hero
        background="art"
        borderTitle
        fontSize="md"
        img={getImageUrl(page.focusart_img, 'large', stub, 'pages')}
        pretitle={page.focusart_pretitle}
        link={page.focusart_link}
        text={page.focusart_text}
        title={insertBrAfterFirstSpace(page.focusart_title)}
        imgSize="crop"
      />
    )
  }

  /*------------------------------
  Render Focus 2
  ------------------------------*/
  const renderFocus2 = () => {
    return page.focus2_title && (
      <Hero
        background="light"
        borderTitle
        fontSize="lg"
        label={page.focus2_label}
        link={page.focus2_link}
        pretitle={page.focus2_pretitle}
        text={page.focus2_text}
        title={page.focus2_title}
        img={getImageUrl(page.focus2_img, 'large', stub, 'pages')}
        imgSize="crop"
      />
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer theme="dark" />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverHome()}
        {renderVideo()}
        {renderFocus()}
        {renderHomeHoverList()}
        {renderFocusProject()}
        {renderFocusArt()}
        {renderFocus2()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageHome ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default HomePage
